+<!--
  Tabbed passport body: VTabs + VTabsWindow; all layout sections per tab (row order from adapter).
-->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import {
  isElementLinkedFieldCell,
  isElementLinkSelectorCell,
  isLinkedReferenceGridLayoutItem,
} from '@/features/Inventory/ObjectTypePassportGrid/model/passportFieldPalette'
import type { RuntimeFieldValueMap } from '@/features/Inventory/ObjectListEdit/model/runtimeFieldBindings'
import { useTranslate } from '@/shared/lib'
import { InventTypeField } from '@/entities/Inventory/TypeField'
import { ListFieldList, ReferenceFieldList } from '@/entities/Inventory/TypeField/model/types'
import {
  ObjectPassportField,
  ObjectPassportLinkedTable,
} from '@/entities/Inventory/ObjectPassportFields'
import '@/entities/Inventory/ObjectPassportFields/ui/assets/style.scss'
import type {
  ObjectPassportFieldsPayload,
  ObjectPassportFieldDefinition,
} from '@/entities/Inventory/ObjectPassportFields'
import {
  isObjectPassportBooleanField,
  isObjectPassportReferenceField,
  isObjectPassportSelectableField,
  objectPassportFieldPlacement,
  objectPassportValueForField,
} from '@/entities/Inventory/ObjectPassportFields/model/objectPassportFieldAdapter'
import { fieldTypeNameFromId } from '@/entities/Inventory/ObjectPassportFields/model/fieldTypeLegacy'
import InventObjectLinkTypeField from '@/entities/Inventory/TypeField/ui/fields/InventObjectLinkTypeField.vue'
import { LogicFieldList } from '@/entities/Inventory/TypeField/model/types'
import { statsLinkedObjectCount, statsSummaryLines } from '../model/passportStatsPreview'
import { isFieldMandatory } from '../model/validateMandatoryFields'
import {
  runtimeFieldCaptionFromSlot,
  runtimePassportFieldLabelFromSlot,
} from '../model/passportRuntimeFieldLabel'
import {
  isRuntimeOwnEditableField,
  resolveRuntimePaletteSource,
} from '../model/runtimeOwnFieldEditable'
import {
  focusFieldIdForPlaceholder,
  parentPillTextForSlot,
  parentRawCopyValue,
  rawValueForPlaceholderCopy,
  unsupportedAttemptedDisplay,
  unsupportedRefBooleanSwitchValue,
} from '../model/passportRuntimeFieldDisplay'
import {
  resolveOwnFieldModalTarget,
  resolveCatalogSelectDisplayValue,
  resolveFieldModalValueSource,
  resolveReadonlyFieldModalTarget,
  resolveUnsupportedFieldModalTarget,
  type RuntimeFieldModalTarget,
} from '../model/passportRuntimeFieldModal'
import PassportRuntimeListTableBlock from './PassportRuntimeListTableBlock.vue'
import PassportRuntimeOwnBooleanFieldCard from './PassportRuntimeOwnBooleanFieldCard.vue'
import PassportRuntimeParentFieldCard from './PassportRuntimeParentFieldCard.vue'
import PassportRuntimeV2FieldSelectModal from './PassportRuntimeV2FieldSelectModal.vue'
import PassportRuntimeUnsupportedFieldCard from './PassportRuntimeUnsupportedFieldCard.vue'
import PassportRuntimeRelatedCards from './PassportRuntimeRelatedCards.vue'
import type { RelatedObjectCard } from '../model/usePassportRuntimeRelated'
import type { PseudosqlValueResult } from '../model/usePassportPseudosqlRecompute'
import type { NormalizedFieldValidationSource } from '../model/enrichInventoryFieldValidationProps'
import type {
  RuntimeFieldSlot,
  RuntimeLayoutItem,
  RuntimeLayoutTab,
  RuntimePlaceholderItem,
} from '../model/runtimeLayoutAdapter'
import { runtimeTabTitle } from '../model/runtimeTabTitle'

const props = withDefaults(
  defineProps<{
    tabs: RuntimeLayoutTab[]
    fieldValues: RuntimeFieldValueMap
    /** Current catalog `invent_object_type.id` — for «собственные поля» vs parent/list. */
    catalogTypeId: number
    /** Historical snapshot: no editing. */
    fieldsLocked?: boolean
    /** Passport API `stats` — aggregates for LIST / перечень blocks. */
    stats?: unknown
    /** Merged type fields — optional type pill on unsupported cells when `layoutItem.id` matches. */
    allFields?: ObjectInventoryField[]
    /** «Справочники» cells: display string from linked passport rows (key = grid `layoutItem.id`). */
    linkedDisplayValues?: Record<string, string>
    /** True while linked passport fetches for parent palette cells are in flight. */
    linkedValuesLoading?: boolean
    /** Route / context for LIST table blocks (row loads in later tasks). */
    objectId: string
    elementId: number
    filter?: { status_date: string | null }
    /** Object-type LIST/tab groups — pass through for link modal `inventory_type` fallback. */
    fieldGroups?: unknown[]
    /** LIST/перечень blocks: orange header only (no embedded rows) — e.g. Create V2 batch staging. */
    listTableHeaderOnly?: boolean
    /** When true, omit the top `VTabs` strip; parent can drive `v-model:activeTab` (window content unchanged). */
    hideTabStrip?: boolean
    /**
     * Create-batch / draft: parent «Справочники» + linked-reference cells use `InventTypeField` like
     * `PassportRuntimeV2AllFieldsDrawer`, instead of read-only parent cards (no linked passport rows yet).
     */
    draftPassportEditing?: boolean
    hasRelatedTab?: boolean
    relatedObjects?: RelatedObjectCard[]
    relatedLoading?: boolean
    /** Field ids that failed mandatory validation on save — drives red highlight in inputs. */
    invalidMandatoryFieldIds?: ReadonlySet<string>
    /** When false, blocks opening the all-fields drawer from layout cells. */
    canOpenDetailedView?: boolean
    /** Блокирует выбор в readonly-карточках для снимка на дату или без права редактирования. */
    fieldSelectionLocked?: boolean
    /** Нормализованный паспорт: рендер полей через ObjectPassportField. */
    useNormalizedFields?: boolean
    normalizedPassportPayload?: ObjectPassportFieldsPayload | null
    /** Пересчитанные pseudosql value_result / error по ключу placement.layout_id. */
    pseudosqlValues?: Record<string, PseudosqlValueResult>
    /** Нормализованные определения полей типа — источник pseudo_sql размещений в draft-режиме. */
    normalizedFieldDefinitions?: NormalizedFieldValidationSource[]
  }>(),
  {
    allFields: () => [],
    linkedDisplayValues: () => ({}),
    linkedValuesLoading: false,
    filter: () => ({ status_date: null }),
    fieldGroups: () => [],
    listTableHeaderOnly: false,
    hideTabStrip: false,
    draftPassportEditing: false,
    hasRelatedTab: false,
    relatedObjects: () => [],
    relatedLoading: false,
    invalidMandatoryFieldIds: () => new Set<string>(),
    canOpenDetailedView: false,
    fieldSelectionLocked: false,
    useNormalizedFields: false,
    normalizedPassportPayload: null,
    pseudosqlValues: () => ({}),
    normalizedFieldDefinitions: () => [],
  },
)

const emit = defineEmits<{
  openDrawer: [fieldId?: string | null]
  patchFieldValue: [payload: { fieldId: string; value: string }]
  'recompute-pseudosql': []
}>()

function onFieldModelUpdate(fieldId: string, next: unknown): void {
  const value = next == null ? '' : String(next)
  if (props.draftPassportEditing) {
    emit('patchFieldValue', { fieldId, value })
    return
  }
  props.fieldValues[fieldId] = value
}

const { trs } = useTranslate()

const normalizedFieldsById = computed(() => {
  const map = new Map<number, ObjectPassportFieldDefinition>()
  for (const field of props.normalizedPassportPayload?.fields ?? []) {
    map.set(field.id, field)
  }
  return map
})

function normalizedFieldForSlot(item: RuntimeFieldSlot): ObjectPassportFieldDefinition | null {
  if (!props.useNormalizedFields) return null

  const numericDirect = Number(item.fieldId)
  if (Number.isFinite(numericDirect) && numericDirect > 0) {
    const direct = normalizedFieldsById.value.get(numericDirect)
    if (direct) return direct
  }

  return null
}

function normalizedValueForSlot(item: RuntimeFieldSlot) {
  const field = normalizedFieldForSlot(item)
  if (!field) return null
  return objectPassportValueForField(field.id, props.normalizedPassportPayload?.values ?? [])
}

const modelActiveTab = defineModel<number>('activeTab', { default: 0 })
const internalActiveTab = ref(0)
const fieldSelectModalOpen = ref(false)
const selectedFieldModal = ref<RuntimeFieldModalTarget | null>(null)
/** Подписи значений, выбранных в модалке (id → name), пока селект грида без options. */
const selectionLabelsByFieldId = ref<Record<string, string>>({})

const activeTab = computed({
  get() {
    if (props.hideTabStrip) return modelActiveTab.value
    return internalActiveTab.value
  },
  set(v: number) {
    if (props.hideTabStrip) {
      modelActiveTab.value = v
    } else {
      internalActiveTab.value = v
    }
  },
})

function tabTitle(tab: RuntimeLayoutTab): string {
  return runtimeTabTitle(tab, trs)
}

function gridColumnStyle(layoutItem: GridFieldType): { gridColumn: string } {
  const len = Math.max(1, Number(layoutItem.length) || 1)
  return { gridColumn: `span ${len}` }
}

/**
 * Текст legacy readonly-ячейки в draft: pseudosql размещения → сырое значение → «—»
 * (как `displayText || '—'` в ObjectPassportField; «Не задан» не используем).
 */
function readonlyFallbackDisplayText(item: RuntimeFieldSlot): string {
  const pseudo = pseudosqlOverridesForSlot(item)?.value_result?.trim()
  if (pseudo) return pseudo
  const raw = props.fieldValues[item.fieldId]
  if (raw === undefined || raw === null || raw === '') return '—'
  return String(raw)
}

function parentPillText(item: RuntimeFieldSlot): string {
  return parentPillTextForSlot(
    item,
    props.fieldValues,
    props.linkedDisplayValues ?? {},
    props.linkedValuesLoading ?? false,
    trs,
  )
}

function parentCopyRaw(item: RuntimeFieldSlot): string {
  return parentRawCopyValue(item, props.fieldValues, props.linkedDisplayValues)
}

function unsupportedDisplay(item: RuntimePlaceholderItem): string {
  const result = unsupportedAttemptedDisplay(
    item,
    props.fieldValues,
    props.linkedDisplayValues ?? {},
    props.linkedValuesLoading ?? false,
    props.allFields ?? [],
    trs,
  )
  return result
}

function unsupportedRaw(item: RuntimePlaceholderItem): string {
  return rawValueForPlaceholderCopy(item, props.allFields ?? [], props.fieldValues)
}

function unsupportedFocusId(item: RuntimePlaceholderItem): string | null {
  return focusFieldIdForPlaceholder(item, props.allFields ?? [])
}

function unsupportedBooleanSwitch(item: RuntimePlaceholderItem): boolean | null {
  return unsupportedRefBooleanSwitchValue(
    item,
    props.allFields ?? [],
    props.linkedDisplayValues ?? {},
    props.linkedValuesLoading ?? false,
    props.draftPassportEditing ? props.fieldValues : undefined,
  )
}

function isParentPaletteField(item: RuntimeFieldSlot): boolean {
  return resolveRuntimePaletteSource(item.layoutItem, item.field, props.catalogTypeId) === 'parent'
}

/** «Справочники» / nested grid ids must stay read-only cards even if palette heuristics misclassify as own. */
function isLinkedReferenceGridCell(item: RuntimeFieldSlot): boolean {
  return isLinkedReferenceGridLayoutItem(item.layoutItem)
}

/** Element-linked read-only field cells resolve their value through the selected linked object. */
function isElementLinkedFieldSlot(item: RuntimeFieldSlot): boolean {
  return isElementLinkedFieldCell(item.layoutItem)
}

/** Element-link selector cell needs the target type so the link field can fetch candidate objects. */
function elementLinkSelectorField(item: RuntimeFieldSlot): ObjectInventoryField {
  const linkFieldId = String(item.layoutItem.link_field_id ?? item.fieldId)
  const linkField = props.allFields?.find((f) => String(f.id) === linkFieldId) ?? item.field
  const targetTypeId = linkField.link_target_type_id ?? Number(linkField.object_type)
  return {
    ...linkField,
    object_type: targetTypeId,
  }
}

function isParentOrLinkedGridCell(item: RuntimeFieldSlot): boolean {
  return isParentPaletteField(item) || isLinkedReferenceGridCell(item)
}

/** Linked / nested grid cells that are not parent-catalog fields — drawer edits these with `InventTypeField`. */
function isLinkedOnlyReferenceCell(item: RuntimeFieldSlot): boolean {
  return isLinkedReferenceGridCell(item) && !isParentPaletteField(item)
}

function isFieldSlot(item: RuntimeLayoutItem): item is RuntimeFieldSlot {
  return item.kind === 'field'
}

/** Own-field slots use the same outlined controls as «текущая дата»; when locked they are readonly (select/copy ok, no edit/paste). */
function showOwnFieldTypeInput(item: RuntimeFieldSlot): boolean {
  if (isParentOrLinkedGridCell(item)) return false
  return isRuntimeOwnEditableField(item.layoutItem, item.field, props.catalogTypeId)
}

/**
 * Main grid: runtime = own fields only; draft batch = same surface as all-fields drawer (own + parent palette + linked ref cells).
 */
function showMainGridFieldInput(item: RuntimeFieldSlot): boolean {
  if (showOwnFieldTypeInput(item)) return true
  if (!props.draftPassportEditing) return false
  if (isParentPaletteField(item)) return true
  if (isLinkedOnlyReferenceCell(item)) return true
  return false
}

/** Табличное размещение (`view_type: table`) — linked-таблица, не скаляр. */
function isNormalizedTableFieldSlot(item: RuntimeFieldSlot): boolean {
  if (!props.useNormalizedFields) return false
  return item.layoutItem.view_type === 'table'
}

/** Скалярное размещение нормализованного поля (`view_type: value`). */
function isNormalizedScalarValueSlot(item: RuntimeFieldSlot): boolean {
  if (!props.useNormalizedFields || props.draftPassportEditing) return false
  return item.layoutItem.view_type !== 'table'
}

/** Скалярные поля справочника и связи с объектом инвентаря — кликабельная карточка + модалка выбора. */
function isNormalizedSelectableValueSlot(item: RuntimeFieldSlot): boolean {
  const field = normalizedFieldForSlot(item)
  return (
    isNormalizedScalarValueSlot(item) && field != null && isObjectPassportSelectableField(field)
  )
}

/** Скаляр только для чтения без модалки выбора (строки, числа, display-SQL и т.д.). */
function isNormalizedReadonlyScalarValueSlot(item: RuntimeFieldSlot): boolean {
  const field = normalizedFieldForSlot(item)
  if (!isNormalizedScalarValueSlot(item) || field == null || isBooleanFieldSlot(item)) return false
  return !isObjectPassportSelectableField(field)
}

/** Layout-ids размещений с pseudo_sql — из нормализованных определений (draft-режим). */
const pseudosqlLayoutIds = computed(() => {
  const ids = new Set<number>()
  for (const def of props.normalizedFieldDefinitions) {
    for (const fp of def.field_props ?? []) {
      if (fp?.id != null && fp.pseudo_sql?.trim()) ids.add(Number(fp.id))
    }
  }
  return ids
})

/** Fallback fieldId → layout id первого размещения (у legacy own-ячеек нет суффикса layout id). */
const firstLayoutIdByFieldId = computed(() => {
  const map = new Map<string, number>()
  for (const def of props.normalizedFieldDefinitions) {
    const lid = def.field_props?.find((fp) => fp?.id != null)?.id
    if (lid == null) continue
    map.set(String(def.id), Number(lid))
    if (def.legacy_field_id) map.set(String(def.legacy_field_id), Number(lid))
  }
  return map
})

/**
 * Id размещения (`field_props.id`): из `layoutItem.field_layout_id` (нормализованный layout),
 * иначе — числовой суффикс legacy-id `parent:<fieldId>:<layoutId>` / `element:<fieldId>:<layoutId>`,
 * иначе — первое размещение поля из нормализованных определений (own-ячейки legacy-грида).
 */
function placementLayoutIdForSlot(item: RuntimeFieldSlot): number | null {
  const direct = Number(item.layoutItem.field_layout_id)
  if (Number.isFinite(direct) && direct > 0) return direct
  const m = String(item.layoutItem.id ?? '').match(/^(?:parent|element):[^:]+:(\d+)$/)
  if (m) {
    const parsed = Number(m[1])
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return firstLayoutIdByFieldId.value.get(String(item.fieldId)) ?? null
}

/** Размещение с pseudo_sql: в паспорте readonly-отображение, в draft зеркалим тем же. */
function placementHasPseudosql(item: RuntimeFieldSlot): boolean {
  const lid = placementLayoutIdForSlot(item)
  return lid != null && pseudosqlLayoutIds.value.has(lid)
}

function isPseudoSqlValueResultSlot(item: RuntimeFieldSlot): boolean {
  const field = normalizedFieldForSlot(item)
  if (!field || !isObjectPassportReferenceField(field)) return false
  const typeName = fieldTypeNameFromId(field.field_type)
  if (typeName !== 'one_to_many' && typeName !== 'many_to_many') return false
  const placement = objectPassportFieldPlacement(field, placementLayoutIdForSlot(item))
  return !!placement?.pseudo_sql?.trim() && !!placement?.value_result?.trim()
}

function pseudosqlOverridesForSlot(item: RuntimeFieldSlot): PseudosqlValueResult | null {
  const lid = placementLayoutIdForSlot(item)
  if (lid == null || !props.pseudosqlValues) return null
  return props.pseudosqlValues[String(lid)] ?? null
}

function isBooleanFieldSlot(item: RuntimeFieldSlot): boolean {
  if (String(item.field.type) === LogicFieldList.BOOLEAN) return true
  if (!props.useNormalizedFields) return false
  const normalized = normalizedFieldForSlot(item)
  return normalized != null && isObjectPassportBooleanField(normalized)
}

function showOwnBooleanPassportCard(item: RuntimeFieldSlot): boolean {
  return showOwnFieldTypeInput(item) && isBooleanFieldSlot(item)
}

/** Типы, которые на edit выбираются через модалку (внешний label + значение в рамке). */
function isInventModalSelectableFieldType(fieldType: string | undefined | null): boolean {
  const t = String(fieldType ?? '').toLowerCase()
  return (
    t === ListFieldList.LIST ||
    t === ListFieldList.LIST_TYPES ||
    t === ReferenceFieldList.OBJECT_LINK ||
    t === 'link_object'
  )
}

/**
 * Create / legacy invent: тот же chrome, что у ObjectPassportField selectable на edit
 * (label сверху, значение в рамке, выбор в модалке) — без floating outlined input.
 */
function showInventSelectableFieldChrome(item: RuntimeFieldSlot): boolean {
  return (
    showMainGridFieldInput(item) &&
    !isBooleanFieldSlot(item) &&
    isInventModalSelectableFieldType(item.field.type) &&
    !normalizedFieldForSlot(item)
  )
}

function inventSelectableShowsInnerInput(item: RuntimeFieldSlot): boolean {
  // Размещение с pseudo_sql — readonly-текст как в паспорте, выбор только через модалку.
  if (placementHasPseudosql(item)) return false
  const t = String(item.field.type ?? '').toLowerCase()
  return t === ListFieldList.LIST || t === ListFieldList.LIST_TYPES
}

function canActivateInventSelectable(item: RuntimeFieldSlot): boolean {
  return canOpenOwnFieldModal(item) && !Boolean(props.fieldsLocked)
}

function onInventSelectableActivate(item: RuntimeFieldSlot): void {
  if (!canActivateInventSelectable(item)) return
  openOwnFieldModal(item)
}

function onOwnBooleanPatch(payload: { fieldId: string; value: string }) {
  onFieldModelUpdate(payload.fieldId, payload.value)
}

/**
 * Проверяет, можно ли открыть модалку выбора для поля в текущем режиме паспорта.
 */
const canOpenFieldModal = (
  fieldId: string | null | undefined,
  field: ObjectInventoryField | null,
): boolean => {
  if (props.fieldSelectionLocked) return false
  if (!props.draftPassportEditing && !props.canOpenDetailedView) return false
  return Boolean(fieldId && field)
}

/**
 * Проверяет readonly-карточку на возможность выбора значения.
 */
const canOpenReadonlyFieldSelect = (item: RuntimeFieldSlot): boolean => {
  const target = resolveReadonlyFieldModalTarget(
    item,
    props.catalogTypeId,
    props.allFields ?? [],
    runtimeFieldCaptionFromSlot(item),
  )
  return canOpenFieldModal(target?.fieldId, target?.field ?? null)
}

/** Открывает модалку и подставляет фактический источник relation-значения из passport response. */
const showFieldSelectModal = (target: RuntimeFieldModalTarget | null): void => {
  if (!target) return
  selectedFieldModal.value = resolveFieldModalValueSource(target, props.normalizedPassportPayload)
  fieldSelectModalOpen.value = true
}

/**
 * Открывает модальное окно выбора для readonly-поля основного отображения.
 */
const openReadonlyFieldSelect = (item: RuntimeFieldSlot): void => {
  if (!canOpenReadonlyFieldSelect(item)) return
  const target = resolveReadonlyFieldModalTarget(
    item,
    props.catalogTypeId,
    props.allFields ?? [],
    runtimeFieldCaptionFromSlot(item),
  )
  showFieldSelectModal(target)
}

/**
 * Применяет значение из модалки в общую карту значений паспорта.
 */
const applyReadonlyFieldSelect = (payload: {
  fieldId: string
  value: string
  label?: string
}): void => {
  onFieldModelUpdate(payload.fieldId, payload.value)
  const label = payload.label?.trim()
  if (label) {
    selectionLabelsByFieldId.value = {
      ...selectionLabelsByFieldId.value,
      [payload.fieldId]: label,
    }
  } else if (!payload.value) {
    const next = { ...selectionLabelsByFieldId.value }
    delete next[payload.fieldId]
    selectionLabelsByFieldId.value = next
  }
  emit('recompute-pseudosql')
}

function selectionLabelForField(fieldId: string): string | null {
  return selectionLabelsByFieldId.value[fieldId] ?? null
}

function inventSelectableFieldData(item: RuntimeFieldSlot): ObjectInventoryField {
  const base = props.allFields?.find((f) => String(f.id) === item.fieldId) ?? item.field
  const rawId = String(props.fieldValues[item.fieldId] ?? '').trim()
  const label = selectionLabelForField(item.fieldId)
  if (!rawId || !label) return base
  const items = Array.isArray(base.items) ? [...base.items] : []
  if (items.some((row) => String(row.id) === rawId)) return base
  const numericId = Number(rawId)
  items.push({
    id: Number.isFinite(numericId) ? numericId : (rawId as unknown as number),
    name: label,
  })
  return { ...base, items }
}

/**
 * Текст readonly-поля выбора в драфте: pseudosql → подпись из модалки → «—»
 * (сырой id элемента не показываем; пустое/неразрешённое — «—», как в ObjectPassportField).
 */
function inventSelectableDisplayText(item: RuntimeFieldSlot): string {
  const pseudo = pseudosqlOverridesForSlot(item)?.value_result?.trim()
  if (pseudo) return pseudo
  const label = selectionLabelForField(item.fieldId)
  if (label) return label
  return '—'
}

/**
 * Проверяет собственную карточку поля на возможность открытия модалки.
 */
const canOpenOwnFieldModal = (item: RuntimeFieldSlot): boolean => {
  const field = props.allFields?.find((f) => String(f.id) === item.fieldId) ?? item.field
  return canOpenFieldModal(item.fieldId, field ?? null)
}

/**
 * Открывает модалку для собственного поля, отображённого карточкой.
 */
const openOwnFieldModal = (item: RuntimeFieldSlot): void => {
  if (!canOpenOwnFieldModal(item)) return
  const target = resolveOwnFieldModalTarget(
    item,
    runtimePassportFieldLabelFromSlot(item),
    props.allFields ?? [],
  )
  showFieldSelectModal(target)
}

/**
 * Проверяет placeholder/ref-карточку на возможность выбора значения.
 */
const canOpenUnsupportedFieldModal = (item: RuntimePlaceholderItem): boolean => {
  const target = resolveUnsupportedFieldModalTarget(item, props.allFields ?? [])
  return canOpenFieldModal(target?.fieldId, target?.field ?? null)
}

/**
 * Открывает модалку для placeholder/ref-карточки.
 */
const openUnsupportedFieldModal = (item: RuntimePlaceholderItem): void => {
  if (!canOpenUnsupportedFieldModal(item)) return
  showFieldSelectModal(resolveUnsupportedFieldModalTarget(item, props.allFields ?? []))
}

const selectedField = computed(() => selectedFieldModal.value?.field ?? null)
const selectedFieldId = computed(() => selectedFieldModal.value?.fieldId ?? null)
const selectedFieldLabel = computed(() => selectedFieldModal.value?.label ?? '')
const selectedFieldValue = computed(() => {
  const id = selectedFieldId.value
  if (!id) return ''
  return props.fieldValues[id] ?? ''
})
const selectedFieldDisplayValue = computed(() => {
  return resolveCatalogSelectDisplayValue(selectedField.value?.id, props.normalizedPassportPayload)
})

function isPlaceholder(item: RuntimeLayoutItem): item is RuntimePlaceholderItem {
  return item.kind === 'placeholder'
}

function placeholderMode(item: RuntimePlaceholderItem): 'heading' | 'other' {
  const t = item.layoutItem.type
  if (t === 'heading') return 'heading'
  return 'other'
}

function isTableLayoutItem(item: RuntimePlaceholderItem): boolean {
  return item.layoutItem.type === 'table'
}

const hasTabs = computed(() => props.tabs.length > 0 || props.hasRelatedTab)
const extendedTabCount = computed(() => props.tabs.length + (props.hasRelatedTab ? 1 : 0))

watch(
  () => extendedTabCount.value,
  (len) => {
    if (activeTab.value >= len) activeTab.value = Math.max(0, len - 1)
  },
)

const statsLines = computed(() => statsSummaryLines(props.stats))
const linkedObjectCount = computed(() => statsLinkedObjectCount(props.stats))

function tabSections(tab: RuntimeLayoutTab) {
  return tab.sections ?? []
}

/** Row-level chrome: hide fallback "Строка N" / "Row N" and titles that repeat the active tab label. */
function showRowSectionHeading(
  tab: RuntimeLayoutTab,
  section: { kind: string; headingLabel: string },
): boolean {
  if (section.kind !== 'row') return true
  const label = section.headingLabel?.trim() ?? ''
  if (!label) return false
  if (/^Строка\s+\d+$/i.test(label)) return false
  if (/^Row\s+\d+$/i.test(label)) return false
  if (tab.id !== 'general' && label === String(tabTitle(tab)).trim()) return false
  return true
}

function showMandatoryErrorForField(fieldId: string): boolean {
  return props.invalidMandatoryFieldIds.has(fieldId)
}
</script>

<template>
  <div class="passport-runtime-v2-body" :data-normalized-passport="useNormalizedFields">
    <template v-if="!hasTabs">
      <p class="passport-runtime-v2__empty-tabs">{{ trs('itf.passport_runtime_empty') }}</p>
    </template>

    <template v-else>
      <VTabs
        v-if="!hideTabStrip"
        v-model="activeTab"
        class="passport-runtime-v2__tabs"
        color="primary"
        density="comfortable"
        align-tabs="start"
      >
        <VTab
          v-for="(tab, index) in tabs"
          :key="tab.id"
          :value="index"
          class="passport-runtime-v2__tab"
        >
          {{ tabTitle(tab) }}
        </VTab>
        <VTab v-if="hasRelatedTab" :value="tabs.length" class="passport-runtime-v2__tab">
          Связанные объекты
        </VTab>
      </VTabs>

      <VTabsWindow v-model="activeTab" class="passport-runtime-v2__tabs-window">
        <VTabsWindowItem
          v-for="(tab, index) in tabs"
          :key="`${tab.id}-window`"
          :value="index"
          class="passport-runtime-v2__tabs-window-item"
        >
          <div class="passport-runtime-v2__tab-panel">
            <div v-if="!tabSections(tab).length" class="passport-runtime-v2__section-empty">
              {{ trs('itf.passport_runtime_empty') }}
            </div>

            <div v-else class="passport-runtime-v2__tab-sections">
              <template v-for="section in tabSections(tab)" :key="section.id">
                <!-- placeholder section (unsupported list/table tab) -->
                <div
                  v-if="section.kind === 'placeholder'"
                  class="passport-runtime-v2__section passport-runtime-v2__section--placeholder"
                >
                  <div
                    class="passport-runtime-v2__section-heading"
                    :class="`passport-runtime-v2__section-heading--${section.headingVariant}`"
                  >
                    {{ section.headingLabel }}
                  </div>
                  <p class="passport-runtime-v2__section-hint">
                    {{ trs('itf.passport_runtime_unsupported_section_hint') }}
                  </p>
                  <ul v-if="statsLines.length" class="passport-runtime-v2__stats-list">
                    <li
                      v-for="(row, ri) in statsLines"
                      :key="`st-${ri}`"
                      class="passport-runtime-v2__stats-list-item"
                    >
                      <span class="passport-runtime-v2__stats-list-label">{{ row.label }}</span>
                      <span class="passport-runtime-v2__stats-list-value">{{ row.value }}</span>
                    </li>
                  </ul>
                  <p v-if="linkedObjectCount != null" class="passport-runtime-v2__stats-meta">
                    {{ trs('itf.passport_runtime_stats_objects') }}: {{ linkedObjectCount }}
                  </p>
                </div>

                <div v-else class="passport-runtime-v2__section passport-runtime-v2__section--row">
                  <div
                    v-if="showRowSectionHeading(tab, section)"
                    class="passport-runtime-v2__section-heading"
                    :class="`passport-runtime-v2__section-heading--${section.headingVariant}`"
                  >
                    {{ section.headingLabel }}
                  </div>

                  <p v-if="!section.items.length" class="passport-runtime-v2__tab-no-fields">
                    {{ trs('itf.passport_runtime_tab_no_fields') }}
                  </p>

                  <div v-else class="passport-runtime-v2__grid-row">
                    <template
                      v-for="(item, itemIdx) in section.items"
                      :key="`${section.id}-i-${itemIdx}`"
                    >
                      <div
                        v-if="isFieldSlot(item)"
                        class="passport-runtime-v2__grid-cell passport-runtime-v2__grid-cell--field"
                        :style="gridColumnStyle(item.layoutItem)"
                      >
                        <PassportRuntimeParentFieldCard
                          v-if="isParentOrLinkedGridCell(item) && !showMainGridFieldInput(item)"
                          :caption="runtimePassportFieldLabelFromSlot(item)"
                          :pill-text="parentPillText(item)"
                          :copy-value="parentCopyRaw(item)"
                          :field-type="String(item.field.type ?? '')"
                          :layout-palette-source="item.layoutItem.palette_source"
                          :clickable="canOpenReadonlyFieldSelect(item)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          @open-drawer="openReadonlyFieldSelect(item)"
                        />
                        <PassportRuntimeOwnBooleanFieldCard
                          v-else-if="showOwnBooleanPassportCard(item)"
                          :item="item"
                          :field-values="fieldValues"
                          :fields-locked="Boolean(fieldsLocked)"
                          :clickable="canOpenOwnFieldModal(item)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          @open-drawer="openOwnFieldModal(item)"
                          @patch-field="onOwnBooleanPatch"
                        />
                        <InventObjectLinkTypeField
                          v-else-if="isElementLinkSelectorCell(item.layoutItem)"
                          class="passport-runtime-v2__field-input"
                          :field="elementLinkSelectorField(item)"
                          v-model="fieldValues[String(item.layoutItem.link_field_id)]"
                          :label="runtimePassportFieldLabelFromSlot(item)"
                          :mandatory="isFieldMandatory(item.field.mandatory)"
                          :show-mandatory-error="
                            showMandatoryErrorForField(String(item.layoutItem.link_field_id))
                          "
                          hide-details
                          :readonly="Boolean(fieldsLocked)"
                        />
                        <ObjectPassportField
                          v-else-if="
                            isNormalizedTableFieldSlot(item) && normalizedFieldForSlot(item)
                          "
                          class="passport-runtime-v2__field-input passport-runtime-v2__field-input--table"
                          :definition="normalizedFieldForSlot(item)!"
                          :value="normalizedValueForSlot(item)"
                          :force-table-view="true"
                          :pseudosql-overrides="pseudosqlOverridesForSlot(item)"
                        >
                          <template #table="{ definition, value }">
                            <ObjectPassportLinkedTable
                              :object-list-id="elementId"
                              :definition="definition"
                              :value="value"
                              :inventory-group-object-id="objectId"
                              :fields-locked="Boolean(fieldsLocked)"
                              :label="runtimePassportFieldLabelFromSlot(item)"
                              :status-date="filter?.status_date ?? null"
                            />
                          </template>
                        </ObjectPassportField>
                        <ObjectPassportField
                          v-else-if="
                            isNormalizedSelectableValueSlot(item) && normalizedFieldForSlot(item)
                          "
                          class="passport-runtime-v2__field-input"
                          :definition="normalizedFieldForSlot(item)!"
                          :value="normalizedValueForSlot(item)"
                          :placement-layout-id="placementLayoutIdForSlot(item)"
                          :force-table-view="false"
                          :model-value="fieldValues[item.fieldId]"
                          :label="runtimePassportFieldLabelFromSlot(item)"
                          :selection-label="selectionLabelForField(item.fieldId)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          readonly
                          :clickable="!isPseudoSqlValueResultSlot(item)"
                          :disabled="Boolean(fieldsLocked) || !canOpenOwnFieldModal(item)"
                          :pseudosql-overrides="pseudosqlOverridesForSlot(item)"
                          @open-select="openOwnFieldModal(item)"
                        />
                        <ObjectPassportField
                          v-else-if="
                            isNormalizedReadonlyScalarValueSlot(item) &&
                            normalizedFieldForSlot(item)
                          "
                          class="passport-runtime-v2__field-input"
                          :definition="normalizedFieldForSlot(item)!"
                          :value="normalizedValueForSlot(item)"
                          :model-value="fieldValues[item.fieldId]"
                          @update:model-value="onFieldModelUpdate(item.fieldId, $event)"
                          :placement-layout-id="placementLayoutIdForSlot(item)"
                          :force-table-view="false"
                          :label="runtimePassportFieldLabelFromSlot(item)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          :readonly="Boolean(fieldsLocked)"
                          :pseudosql-overrides="pseudosqlOverridesForSlot(item)"
                        />
                        <ObjectPassportField
                          v-else-if="
                            showMainGridFieldInput(item) &&
                            normalizedFieldForSlot(item) &&
                            !isBooleanFieldSlot(item)
                          "
                          class="passport-runtime-v2__field-input"
                          :definition="normalizedFieldForSlot(item)!"
                          :value="normalizedValueForSlot(item)"
                          :model-value="fieldValues[item.fieldId]"
                          @update:model-value="onFieldModelUpdate(item.fieldId, $event)"
                          :label="runtimePassportFieldLabelFromSlot(item)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          :readonly="Boolean(fieldsLocked)"
                          :pseudosql-overrides="pseudosqlOverridesForSlot(item)"
                        />
                        <div
                          v-else-if="showInventSelectableFieldChrome(item)"
                          class="passport-runtime-v2__field-input object-passport-field__outlined-readonly"
                          :class="{
                            'object-passport-field__outlined-readonly--clickable':
                              canActivateInventSelectable(item),
                          }"
                          :role="canActivateInventSelectable(item) ? 'button' : undefined"
                          :tabindex="canActivateInventSelectable(item) ? 0 : undefined"
                          @click="onInventSelectableActivate(item)"
                          @keydown.enter.prevent="onInventSelectableActivate(item)"
                          @keydown.space.prevent="onInventSelectableActivate(item)"
                        >
                          <InventTypeField
                            v-if="inventSelectableShowsInnerInput(item)"
                            class="object-passport-field__selectable-readonly-input"
                            :type="String(inventSelectableFieldData(item).type)"
                            :field-data="inventSelectableFieldData(item)"
                            :model-value="fieldValues[item.fieldId]"
                            :label="runtimePassportFieldLabelFromSlot(item)"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            readonly
                            :disabled="Boolean(fieldsLocked)"
                            :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          />
                          <VTextField
                            v-else
                            class="object-passport-field__display-text-input"
                            :model-value="inventSelectableDisplayText(item)"
                            :label="runtimePassportFieldLabelFromSlot(item)"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            :append-inner-icon="
                              canActivateInventSelectable(item)
                                ? 'keyboard_arrow_down'
                                : undefined
                            "
                            readonly
                            :disabled="Boolean(fieldsLocked)"
                            :error="showMandatoryErrorForField(item.fieldId) ? true : undefined"
                            :error-messages="
                              showMandatoryErrorForField(item.fieldId)
                                ? trs('validate.required')
                                : undefined
                            "
                          />
                          <div
                            v-if="pseudosqlOverridesForSlot(item)?.error"
                            class="object-passport-field__error"
                          >
                            {{ pseudosqlOverridesForSlot(item)?.error }}
                          </div>
                        </div>
                        <InventTypeField
                          v-else-if="showMainGridFieldInput(item)"
                          class="passport-runtime-v2__field-input"
                          :type="String(item.field.type)"
                          :field-data="item.field"
                          :model-value="fieldValues[item.fieldId]"
                          @update:model-value="onFieldModelUpdate(item.fieldId, $event)"
                          :label="runtimePassportFieldLabelFromSlot(item)"
                          :show-mandatory-error="showMandatoryErrorForField(item.fieldId)"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          :readonly="Boolean(fieldsLocked)"
                        />
                        <div v-else class="passport-runtime-v2__field-input">
                          <VTextField
                            class="object-passport-field__display-text-input"
                            :model-value="readonlyFallbackDisplayText(item)"
                            :label="runtimePassportFieldLabelFromSlot(item)"
                            variant="outlined"
                            density="comfortable"
                            hide-details
                            readonly
                            :error="showMandatoryErrorForField(item.fieldId) ? true : undefined"
                            :error-messages="
                              showMandatoryErrorForField(item.fieldId)
                                ? trs('validate.required')
                                : undefined
                            "
                          />
                          <div
                            v-if="pseudosqlOverridesForSlot(item)?.error"
                            class="object-passport-field__error"
                          >
                            {{ pseudosqlOverridesForSlot(item)?.error }}
                          </div>
                        </div>
                      </div>

                      <div
                        v-else-if="isPlaceholder(item) && placeholderMode(item) === 'heading'"
                        class="passport-runtime-v2__grid-cell passport-runtime-v2__section-heading passport-runtime-v2__section-heading--in-grid"
                        :class="`passport-runtime-v2__section-heading--${section.headingVariant}`"
                        :style="gridColumnStyle(item.layoutItem)"
                      >
                        <span
                          class="passport-runtime-v2__heading-cell-text"
                          :title="item.layoutItem.name"
                        >
                          {{ item.layoutItem.name }}
                        </span>
                      </div>

                      <div
                        v-else-if="isPlaceholder(item) && isTableLayoutItem(item)"
                        class="passport-runtime-v2__grid-cell passport-runtime-v2__grid-cell--table"
                        :style="gridColumnStyle(item.layoutItem)"
                      >
                        <PassportRuntimeListTableBlock
                          :layout-item="item.layoutItem"
                          :object-id="objectId"
                          :element-id="elementId"
                          :header-only="Boolean(listTableHeaderOnly)"
                          :filter="filter"
                          :all-fields="allFields"
                          :fields-locked="Boolean(fieldsLocked)"
                          :field-groups="fieldGroups"
                          :stats="stats"
                        />
                      </div>

                      <div
                        v-else-if="isPlaceholder(item)"
                        class="passport-runtime-v2__grid-cell passport-runtime-v2__grid-cell--unsupported"
                        :style="gridColumnStyle(item.layoutItem)"
                      >
                        <PassportRuntimeUnsupportedFieldCard
                          :item="item"
                          :catalog-type-id="catalogTypeId"
                          :all-fields="allFields"
                          :display-text="unsupportedDisplay(item)"
                          :raw-copy="unsupportedRaw(item)"
                          :focus-field-id="unsupportedFocusId(item)"
                          :readonly-boolean-switch="unsupportedBooleanSwitch(item)"
                          :clickable="canOpenUnsupportedFieldModal(item)"
                          :show-mandatory-error="
                            showMandatoryErrorForField(String(unsupportedFocusId(item) ?? ''))
                          "
                          @open-drawer="openUnsupportedFieldModal(item)"
                        />
                      </div>
                    </template>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </VTabsWindowItem>

        <VTabsWindowItem
          v-if="hasRelatedTab"
          :value="tabs.length"
          class="passport-runtime-v2__tabs-window-item"
        >
          <div class="passport-runtime-v2__tab-panel">
            <div
              class="passport-runtime-v2__section-heading passport-runtime-v2__section-heading--row"
            >
              Связанные объекты
            </div>
            <PassportRuntimeRelatedCards :cards="relatedObjects" :loading="relatedLoading" />
          </div>
        </VTabsWindowItem>
      </VTabsWindow>
    </template>

    <PassportRuntimeV2FieldSelectModal
      v-model:show="fieldSelectModalOpen"
      :field="selectedField"
      :field-id="selectedFieldId"
      :model-value="selectedFieldValue"
      :display-value="selectedFieldDisplayValue"
      :label="selectedFieldLabel"
      :fields-locked="Boolean(fieldSelectionLocked)"
      @apply="applyReadonlyFieldSelect"
    />
  </div>
</template>

<style scoped>
.passport-runtime-v2__grid-cell--field > .passport-runtime-v2__field-label {
  flex: 0 0 auto;
}

.passport-runtime-v2__grid-cell--field > .passport-runtime-v2__field-value {
  flex: 1 1 auto;
}

.passport-runtime-v2__field-label--error {
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__field-value--error {
  box-sizing: border-box;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__grid-cell--table > .prt-list-table {
  min-height: 100%;
  box-sizing: border-box;
}

.passport-runtime-v2__grid-cell.passport-runtime-v2__section-heading--in-grid
  > .passport-runtime-v2__heading-cell-text {
  flex: 1 1 auto;
}
</style>
